import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Users as UsersIcon, GripVertical } from "lucide-react";

/**
 * /admin/team-roles — admin CRUD for the dynamic Team page roles.
 * Each role can be assigned to an existing approved non-client user. Photo,
 * name and affiliation are auto-pulled from that user's profile.
 */
export default function AdminTeamRolesPage() {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRole, setNewRole] = useState({ role_name: "", display_order: "", assigned_user_id: "", description: "", show_on_public_team: true, team_section: "", parent_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/team-roles"),
        api.get("/admin/team-roles/assignable-users"),
      ]);
      setRoles(r1.data || []);
      setUsers(r2.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newRole.role_name.trim()) { toast.error("Role name required"); return; }
    try {
      await api.post("/admin/team-roles", newRole);
      toast.success("Role created");
      setNewRole({ role_name: "", display_order: 100, assigned_user_id: "", description: "", show_on_public_team: true, team_section: "", parent_id: "" });
      load();
    } catch (e) { toast.error("Failed to create role"); }
  };

  const update = async (role) => {
    try {
      const { id, ...payload } = role;
      await api.put(`/admin/team-roles/${id}`, payload);
      toast.success("Role saved");
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const remove = async (role) => {
    if (!window.confirm(`Delete role "${role.role_name}"?`)) return;
    try {
      await api.delete(`/admin/team-roles/${role.id}`);
      toast.success("Role deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-team-roles-page">
      <div>
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">TEAM</div>
        <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
          Dynamic <em className="italic text-kb-purple">team roles</em>
        </h1>
        <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
          Create roles (Director, Research Head, Marketing…) and assign an existing approved user.
          The public /team page renders these dynamically with the user's profile photo.
        </p>
      </div>

      {/* New role */}
      <div className="mt-6 kb-card p-5">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple mb-3">NEW ROLE</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input
            className="kb-input text-sm"
            placeholder="Role name (e.g. Founder, Director, Research Head)"
            value={newRole.role_name}
            onChange={(e) => setNewRole({ ...newRole, role_name: e.target.value })}
            data-testid="team-role-new-name"
          />
          <input
            className="kb-input text-sm"
            type="number"
            min="1"
            placeholder="Sort order (lower = top)"
            value={newRole.display_order ?? ""}
            onChange={(e) => setNewRole({ ...newRole, display_order: e.target.value === "" ? "" : (parseInt(e.target.value, 10) || "") })}
            data-testid="team-role-new-order"
          />
          <select
            className="kb-input text-sm"
            value={newRole.assigned_user_id}
            onChange={(e) => setNewRole({ ...newRole, assigned_user_id: e.target.value })}
            data-testid="team-role-new-user"
          >
            <option value="">— Assign user —</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>{u.name} · {u.role}</option>
            ))}
          </select>
          <input
            className="kb-input text-sm lg:col-span-1"
            placeholder="Short description"
            value={newRole.description}
            onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
            data-testid="team-role-new-description"
          />
          <input
            className="kb-input text-sm"
            placeholder="Team / section (e.g. Research Team › Engineering)"
            value={newRole.team_section}
            onChange={(e) => setNewRole({ ...newRole, team_section: e.target.value })}
            data-testid="team-role-new-section"
          />
          <select
            className="kb-input text-sm"
            value={newRole.parent_id || ""}
            onChange={(e) => setNewRole({ ...newRole, parent_id: e.target.value })}
            data-testid="team-role-new-parent"
          >
            <option value="">— No parent (top level) —</option>
            {roles.map((r) => (<option key={r.id} value={r.id}>{r.role_name}{r.team_section ? ` · ${r.team_section}` : ""}</option>))}
          </select>
          <button onClick={create} className="kb-btn-primary text-sm justify-center" data-testid="team-role-new-create">
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 kb-mono text-xs text-kb-ink-muted" data-testid="team-roles-list">Loading…</div>
      ) : roles.length === 0 ? (
        <div className="mt-10 text-center py-20 border border-dashed border-kb-divider rounded-sm bg-white" data-testid="team-roles-list">
          <UsersIcon className="w-7 h-7 text-kb-ink-light mx-auto" strokeWidth={1.5} />
          <h3 className="font-serif text-xl text-kb-ink mt-4">No team roles defined</h3>
          <p className="text-sm text-kb-ink-muted mt-2">Create the first role above. It will appear on the public /team page once assigned.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-3" data-testid="team-roles-list">
          {roles.map((r) => (
            <div key={r.id} className="kb-card p-4 grid sm:grid-cols-12 gap-3 items-center" data-testid={`team-role-row-${r.id}`}>
              <GripVertical className="w-4 h-4 text-kb-ink-light hidden sm:block" />
              <div className="sm:col-span-2">
                <input
                  className="kb-input text-sm w-full"
                  value={r.role_name}
                  onChange={(e) => setRoles((rs) => rs.map((x) => x.id === r.id ? { ...x, role_name: e.target.value } : x))}
                />
              </div>
              <input
                className="kb-input text-sm w-20"
                type="number"
                value={r.display_order ?? ""}
                onChange={(e) => setRoles((rs) => rs.map((x) => x.id === r.id ? { ...x, display_order: e.target.value === "" ? "" : (parseInt(e.target.value, 10) || 100) } : x))}
              />
              <select
                className="kb-input text-sm sm:col-span-3"
                value={r.assigned_user_id || ""}
                onChange={(e) => setRoles((rs) => rs.map((x) => x.id === r.id ? { ...x, assigned_user_id: e.target.value || null } : x))}
              >
                <option value="">— Unassigned —</option>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.name} · {u.role}</option>
                ))}
              </select>
              <input
                className="kb-input text-sm sm:col-span-3"
                value={r.description || ""}
                placeholder="Short description"
                onChange={(e) => setRoles((rs) => rs.map((x) => x.id === r.id ? { ...x, description: e.target.value } : x))}
              />
              <input
                className="kb-input text-sm sm:col-span-2"
                value={r.team_section || ""}
                placeholder="Section (e.g. Research › ML)"
                onChange={(e) => setRoles((rs) => rs.map((x) => x.id === r.id ? { ...x, team_section: e.target.value } : x))}
              />
              <select
                className="kb-input text-sm sm:col-span-2"
                value={r.parent_id || ""}
                onChange={(e) => setRoles((rs) => rs.map((x) => x.id === r.id ? { ...x, parent_id: e.target.value || null } : x))}
              >
                <option value="">— No parent —</option>
                {roles.filter((p) => p.id !== r.id).map((p) => (
                  <option key={p.id} value={p.id}>{p.role_name}</option>
                ))}
              </select>
              <label className="text-xs text-kb-ink-muted inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={!!r.show_on_public_team}
                  onChange={(e) => setRoles((rs) => rs.map((x) => x.id === r.id ? { ...x, show_on_public_team: e.target.checked } : x))}
                />
                Public
              </label>
              <div className="flex gap-1.5 justify-end">
                <button onClick={() => update(r)} className="kb-btn-primary text-xs" data-testid={`team-role-save-${r.id}`}>
                  <Save className="w-3 h-3" /> Save
                </button>
                <button onClick={() => remove(r)} className="text-rose-600 p-2 hover:bg-rose-50 rounded" data-testid={`team-role-delete-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {r.assigned_user && (
                <div className="sm:col-span-12 flex items-center gap-3 pt-2 border-t border-kb-divider mt-1">
                  {r.assigned_user.picture ? (
                    <img src={r.assigned_user.picture} alt={r.assigned_user.name} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-kb-subtle text-kb-purple flex items-center justify-center text-xs font-bold">
                      {r.assigned_user.initials}
                    </div>
                  )}
                  <div className="text-xs text-kb-ink-muted">
                    <span className="font-semibold text-kb-ink">{r.assigned_user.name}</span>
                    {r.assigned_user.affiliation && <span> · {r.assigned_user.affiliation}</span>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
